import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Heat Index — Rothfusz regression (NWS)
// Supports two sub-calculators: Relative Humidity or Dew Point.

const toF = (v, u) => u === 'F' ? v : u === 'C' ? v*9/5+32 : (v-273.15)*9/5+32;
const fromF = (F, u) => u === 'F' ? F : u === 'C' ? (F-32)*5/9 : (F-32)*5/9 + 273.15;

// compute RH from dew point (Magnus)
function rhFromDew(Tc, Dc) {
  const a=17.27, b=237.7;
  const es = (T) => Math.exp((a*T)/(b+T));
  return Math.max(0, Math.min(100, 100 * es(Dc) / es(Tc)));
}

export default function HeatIndexCalc() {
  const [mode, setMode] = useState('rh'); // rh | dew
  const [t, setT] = useState(90);
  const [tU, setTU] = useState('F');
  const [rh, setRh] = useState(70);
  const [dew, setDew] = useState(70);
  const [dU, setDU] = useState('F');

  const result = useMemo(() => {
    const T = toF(Number(t), tU);
    let R = Number(rh);
    if (mode === 'dew') {
      const Tc = (T - 32)*5/9;
      const Dc = ((toF(Number(dew), dU)) - 32)*5/9;
      R = rhFromDew(Tc, Dc);
    }
    if (T < 80) return { F: T, R };
    const hi = -42.379 + 2.04901523*T + 10.14333127*R - 0.22475541*T*R - 0.00683783*T*T - 0.05481717*R*R + 0.00122874*T*T*R + 0.00085282*T*R*R - 0.00000199*T*T*R*R;
    return { F: hi, R };
  }, [mode, t, tU, rh, dew, dU]);

  const C = (result.F - 32)*5/9;
  const K = C + 273.15;

  let category = 'Caution', color = '#f59e0b';
  if (result.F < 90) { category = 'Comfortable'; color = '#10b981'; }
  else if (result.F < 105) { category = 'Extreme Caution'; color = '#f59e0b'; }
  else if (result.F < 130) { category = 'Danger'; color = '#ef4444'; }
  else { category = 'Extreme Danger'; color = '#7f1d1d'; }

  return (
    <CalcShell look="cnet" title="Heat Index Calculator" subtitle="Compute the ‘feels like’ temperature using humidity or dew-point inputs.">
      <div className="flex gap-2 border-b mb-4">
        {[['rh','Use Relative Humidity'],['dew','Use Dew Point']].map(([v,l]) => (
          <button key={v} className={`px-4 py-2 text-sm font-semibold ${mode===v?'border-b-2 border-blue-600 text-blue-700':'text-slate-500'}`} onClick={() => setMode(v)}>{l}</button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Air Temperature</label>
            <div className="flex gap-2"><input type="number" value={t} onChange={e => setT(e.target.value)} className="flex-1"/>
              <select value={tU} onChange={e => setTU(e.target.value)}><option>F</option><option>C</option><option>K</option></select>
            </div>
          </div>
          {mode === 'rh' ? (
            <div className="field"><label>Relative Humidity (%)</label><input type="number" value={rh} onChange={e => setRh(e.target.value)}/></div>
          ) : (
            <div className="field"><label>Dew Point</label>
              <div className="flex gap-2"><input type="number" value={dew} onChange={e => setDew(e.target.value)} className="flex-1"/>
                <select value={dU} onChange={e => setDU(e.target.value)}><option>F</option><option>C</option><option>K</option></select>
              </div>
            </div>
          )}
        </div>
        <div>
          <div className="result-panel">
            <div className="result-title">Heat Index</div>
            <div className="result-value" style={{ color }}>{result.F.toFixed(1)}°F</div>
            <div className="text-sm text-slate-600 mt-1">{C.toFixed(1)}°C · {K.toFixed(1)} K</div>
            <div className="font-semibold mt-2" style={{ color }}>{category}</div>
            {mode === 'dew' && <div className="text-xs text-slate-500 mt-2">Implied RH: {result.R.toFixed(1)}%</div>}
          </div>
        </div>
      </div>
    </CalcShell>
  );
}
